import { Injectable } from "@angular/core";
import { BasiqValidator } from "@bitwarden/web-vault/app/services/syncing/basiq/validators/basiq.validator";
import {
  BasiqJobStep,
  BasiqJobType,
  GlossGetBasiqJobResponse,
} from "@bitwarden/web-vault/app/models/types/basiq.types";
import { SyncStatusEnum } from "@bitwarden/web-vault/app/models/types/general-types";

const DELAY_FOR_JOB_CHECKS = 5000;

@Injectable({ providedIn: "root" })
export class BasiqJobValidator extends BasiqValidator {
  syncStatus: SyncStatusEnum;

  /** return true if a job from Basiq is ready, false if fails and call itself again after 5 seconds if pending*/
  async isValid(job: BasiqJobType): Promise<boolean> {
    try {
      const activeProcess = this.appState.activeProcesses;
      if (!activeProcess.has("basiq-syncing-data")) {
        this.appState.startProcess("basiq-syncing-data", "Checking connection status");
      }

      const basiqJobResponse = await this.basiqAuthApi.job(job.id);
      if (!basiqJobResponse) {
        this.appState.endProcess("basiq-syncing-data");
        return false;
      }

      if (await this.isJobFailed(basiqJobResponse.job)) {
        this.appState.endProcess("basiq-syncing-data");
        return false;
      }

      if (await this.isJobSuccess(basiqJobResponse.job)) {
        this.appState.endProcess("basiq-syncing-data");
        return true;
      }

      return this.basiqAuthApi.delay(DELAY_FOR_JOB_CHECKS).then(async () => {
        return await this.isValid(job);
      });
    } catch (error) {
      await this.glossSyncService.logError(
        [],
        error,
        "Checking job validation from basiq",
        SyncStatusEnum.basiqJobFetchFail,
      );
      this.syncStatus = SyncStatusEnum.basiqJobFetchFail;
      this.appState.endProcess("basiq-syncing-data");
      return false;
    }
  }

  private async isValidJobResponse(response: GlossGetBasiqJobResponse) {
    try {
      if (!response) {
        await this.glossSyncService.logError(
          [],
          new Error("Invalid response from Basiq for job check"),
          "Checking job validity",
          SyncStatusEnum.basiqJobFetchFail,
        );
        this.syncStatus = SyncStatusEnum.basiqJobFetchFail;
        return false;
      }

      return true;
    } catch (error) {
      await this.glossSyncService.logError(
        [],
        error,
        "Checking job validity",
        SyncStatusEnum.basiqJobFetchFail,
      );
      return false;
    }
  }

  private async isJobFailed(job: BasiqJobType): Promise<boolean> {
    try {
      const isFailed = job.steps.some((step: BasiqJobStep) => step.status === "failed");

      if (isFailed) {
        const error = new Error(JSON.stringify(job));
        await this.glossSyncService.logError(
          [],
          error,
          "Checking if Basiq job is failed",
          SyncStatusEnum.basiqJobFetchFail,
        );
        this.syncStatus = SyncStatusEnum.basiqJobFetchFail;
        return true;
      }

      return false;
    } catch (error) {
      await this.glossSyncService.logError(
        [],
        error,
        "Checking if Basiq job is failed(Caught error)",
        SyncStatusEnum.basiqJobFetchFail,
      );
      return true;
    }
  }

  private async isJobSuccess(job: BasiqJobType): Promise<boolean> {
    try {
      return job.steps.every((step: BasiqJobStep) => step.status === "success");
    } catch (error) {
      await this.glossSyncService.logError(
        [],
        error,
        "Checking if Basiq job is success(Caught error)",
        SyncStatusEnum.basiqJobFetchFail,
      );
      this.syncStatus = SyncStatusEnum.basiqJobFetchFail;
      throw error;
    }
  }

  private async isResolved(job: BasiqJobType): Promise<boolean> {
    return this.isValid(job);
  }

  async resolveJobs(jobs: BasiqJobType[]): Promise<boolean> {
    for (const job of jobs) {
      await this.isResolved(job);
    }
    return true;
  }
}
